import type { ContractCategory } from '@/features/contracts';
import { type ApiInvoice, projectApiInvoice } from '@/features/faturas';
import type {
  ConsumoOrigemRef,
  ConsumoOrigemTipo,
  MetodoPagamento,
} from '@/features/faturas/types';

import type {
  AlertaFinanceiroGlobal,
  ConsumoEmAberto,
  Estorno,
  MetodoDevolucao,
  PainelFinanceiroResumo,
  PlanoContratado,
  PlanoContratadoStatus,
  PlanoPeriodicidade,
  PrevisaoProximaFatura,
  SituacaoAtual,
  SituacaoAtualTipo,
  TipoClienteEscritorio,
} from '../types';

type ApiSituationType =
  | 'NO_INVOICES'
  | 'ALL_SETTLED'
  | 'OVERDUE'
  | 'ISSUE_ERROR'
  | 'DUE_SOON'
  | 'OPEN';

type ApiBillingPeriodicity = 'MONTHLY' | 'YEARLY' | 'ONE_TIME' | 'HYBRID';
type ApiClientType = 'CONTRACTED' | 'ONE_TIME';
type ApiContractStatus = 'ACTIVE' | 'TRIAL' | 'SUSPENDED' | 'CANCELED' | 'EXPIRED';
type ApiPaymentMethod = 'PIX' | 'BOLETO' | 'CARD';

interface ApiCurrentSituation {
  type: ApiSituationType;
  invoice: ApiInvoice | null;
  daysUntilDue: number | null;
  daysOverdue: number | null;
}

interface ApiNextInvoiceForecast {
  available: boolean;
  estimatedValue: number | null;
  periodStart: string | null;
  periodEnd: string | null;
  predictedIssueAt: string | null;
  isTrial: boolean;
  trialMessage: string | null;
  alternativeMessage: string | null;
}

interface ApiContractedPlan {
  id: string;
  productName: string;
  shortDescription: string | null;
  category: ContractCategory | null;
  periodicity: ApiBillingPeriodicity;
  clientType: ApiClientType;
  dueDay: number | null;
  defaultPaymentMethod: ApiPaymentMethod;
  baseValue: number | null;
  status: ApiContractStatus;
  contractedAt: string;
  isMainPlan: boolean;
}

export interface ApiWorkspaceBillingPanel {
  currentSituation: ApiCurrentSituation;
  nextInvoiceForecast: ApiNextInvoiceForecast;
  contractedProducts: ApiContractedPlan[];
}

const SITUATION_MAP: Record<ApiSituationType, SituacaoAtualTipo> = {
  NO_INVOICES: 'SEM_FATURAS',
  ALL_SETTLED: 'TUDO_EM_DIA',
  OVERDUE: 'FATURA_VENCIDA',
  ISSUE_ERROR: 'FATURA_ERRO_EMISSAO',
  DUE_SOON: 'FATURA_PROXIMA_VENCIMENTO',
  OPEN: 'FATURA_EM_ABERTO',
};

const PERIODICITY_MAP: Record<ApiBillingPeriodicity, PlanoPeriodicidade> = {
  MONTHLY: 'MENSAL',
  YEARLY: 'ANUAL',
  ONE_TIME: 'AVULSO',
  HYBRID: 'HIBRIDO',
};

const CLIENT_TYPE_MAP: Record<ApiClientType, TipoClienteEscritorio> = {
  CONTRACTED: 'CONVENIADO',
  ONE_TIME: 'AVULSO',
};

const PAYMENT_METHOD_MAP: Record<ApiPaymentMethod, MetodoPagamento> = {
  PIX: 'PIX',
  BOLETO: 'BOLETO',
  CARD: 'CARTAO',
};

const CONTRACT_STATUS_MAP: Record<ApiContractStatus, PlanoContratadoStatus> = {
  ACTIVE: 'ATIVO',
  TRIAL: 'TRIAL',
  SUSPENDED: 'SUSPENSO',
  CANCELED: 'SUSPENSO',
  EXPIRED: 'SUSPENSO',
};

function projectSituacao(api: ApiCurrentSituation): SituacaoAtual {
  return {
    tipo: SITUATION_MAP[api.type],
    faturaAtiva: api.invoice ? projectApiInvoice(api.invoice) : null,
    diasAteVencimento: api.daysUntilDue,
    diasEmAtraso: api.daysOverdue,
  };
}

function projectPrevisao(api: ApiNextInvoiceForecast): PrevisaoProximaFatura {
  return {
    disponivel: api.available,
    valorEstimado: api.estimatedValue,
    periodoInicio: api.periodStart,
    periodoFim: api.periodEnd,
    dataPrevistaEmissao: api.predictedIssueAt,
    ehTrial: api.isTrial,
    trialMensagem: api.trialMessage,
    mensagemAlternativa: api.alternativeMessage,
  };
}

function projectPlano(api: ApiContractedPlan): PlanoContratado {
  return {
    id: api.id,
    produtoNome: api.productName,
    descricaoCurta: api.shortDescription,
    categoria: api.category ?? null,
    periodicidade: PERIODICITY_MAP[api.periodicity],
    tipoCliente: CLIENT_TYPE_MAP[api.clientType],
    diaVencimento: api.dueDay,
    metodoPagamentoPadrao: PAYMENT_METHOD_MAP[api.defaultPaymentMethod],
    valorBase: api.baseValue,
    status: CONTRACT_STATUS_MAP[api.status],
    contratadoEm: api.contractedAt,
    ehPlanoPrincipal: api.isMainPlan,
  };
}

function buildAlertaGlobal(situacao: SituacaoAtual): AlertaFinanceiroGlobal | null {
  const fatura = situacao.faturaAtiva;
  if (!fatura) return null;

  if (situacao.tipo === 'FATURA_VENCIDA') {
    const dias = situacao.diasEmAtraso ?? 1;
    return {
      faturaId: fatura.id,
      numero: fatura.numero,
      valor: fatura.valorTotal,
      dataVencimento: fatura.dataVencimento,
      nivel: 'DANGER',
      mensagem: `Fatura ${fatura.numero} venceu há ${dias} ${dias === 1 ? 'dia' : 'dias'}`,
    };
  }

  if (situacao.tipo === 'FATURA_PROXIMA_VENCIMENTO') {
    const dias = situacao.diasAteVencimento ?? 0;
    const textoDias = dias <= 0 ? 'hoje' : dias === 1 ? 'amanhã' : `em ${dias} dias`;
    return {
      faturaId: fatura.id,
      numero: fatura.numero,
      valor: fatura.valorTotal,
      dataVencimento: fatura.dataVencimento,
      nivel: 'WARNING',
      mensagem: `Fatura ${fatura.numero} vence ${textoDias}`,
    };
  }

  return null;
}

export function projectWorkspaceBillingPanel(
  api: ApiWorkspaceBillingPanel,
): PainelFinanceiroResumo {
  const situacaoAtual = projectSituacao(api.currentSituation);
  return {
    situacaoAtual,
    previsaoProximaFatura: projectPrevisao(api.nextInvoiceForecast),
    produtosContratados: api.contractedProducts.map(projectPlano),
    alertaGlobal: buildAlertaGlobal(situacaoAtual),
  };
}

interface ApiConsumptionOriginRef {
  type: ConsumoOrigemTipo;
  identifier: string;
  label: string;
}

export interface ApiPayableItem {
  id: string;
  eventIds: string[];
  date: string;
  category: string;
  serviceLabel: string;
  source: ApiConsumptionOriginRef;
  links: ApiConsumptionOriginRef[];
  recipientName: string | null;
  recipientDocument: string | null;
  value: number;
  note: string | null;
}

function projectPayableOrigem(ref: ApiConsumptionOriginRef): ConsumoOrigemRef {
  return { tipo: ref.type, identificador: ref.identifier, rotulo: ref.label };
}

type ApiRefundMethod = 'PIX' | 'TED' | 'CARD_REVERSAL' | 'ACCOUNT_CREDIT';

export interface ApiRefund {
  id: string;
  invoiceId: string;
  invoiceNumber: string;
  originalValue: number;
  refundedValue: number;
  isPartial: boolean;
  reason: string;
  refundMethod: ApiRefundMethod;
  requestedAt: string;
  completedAt: string;
  requestedBy: string;
}

const REFUND_METHOD_MAP: Record<ApiRefundMethod, MetodoDevolucao> = {
  PIX: 'PIX',
  TED: 'TED',
  CARD_REVERSAL: 'ESTORNO_CARTAO',
  ACCOUNT_CREDIT: 'CREDITO_CONTA',
};

export function projectRefund(api: ApiRefund): Estorno {
  return {
    id: api.id,
    faturaId: api.invoiceId,
    faturaNumero: api.invoiceNumber,
    faturaValorOriginal: api.originalValue,
    valorEstornado: api.refundedValue,
    ehEstornoParcial: api.isPartial,
    status: 'CONCLUIDO',
    motivo: api.reason,
    motivoDetalhado: null,
    metodoDevolucao: REFUND_METHOD_MAP[api.refundMethod],
    contaDestinoMascarada: null,
    solicitadoEm: api.requestedAt,
    concluidoEm: api.completedAt,
    solicitadoPor: api.requestedBy,
    observacaoArbitralis: null,
  };
}

export function projectPayableItem(api: ApiPayableItem): ConsumoEmAberto {
  return {
    id: api.id,
    eventIds: api.eventIds,
    data: api.date,
    categoria: api.category,
    servicoLabel: api.serviceLabel,
    origemPrincipal: projectPayableOrigem(api.source),
    vinculacoes: api.links.map(projectPayableOrigem),
    destinatarioNome: api.recipientName,
    destinatarioDocumento: api.recipientDocument,
    valor: api.value,
    observacao: api.note,
    status: 'DISPONIVEL',
  };
}
